/**
 * TeamMemberWizard
 *
 * 3-step wizard to add (or edit) a team member.
 *
 *   Step 1 — Personal details, address, role.
 *            On "Next" (create mode):
 *              POST /ui-view-profiles  → seeded with SERVICE_RECORDS
 *              POST /profiles          → seeded with SERVICE_RECORDS:READ
 *              POST /staff             → with both profile UUIDs attached
 *            On any failure mid-trio: compensating DELETE of whatever
 *            succeeded, then surface error so the user can retry cleanly.
 *
 *            On "Next" (edit mode): PUT /staff with details.
 *
 *   Step 2 — UI View Profile picker.
 *            On "Next": PUT /ui-view-profiles/{uuid} with the new allow-list.
 *
 *   Step 3 — Permission Profile picker.
 *            On "Finish": PUT /profiles/{uuid} with the new permission list.
 *
 * Idempotent retry: profile names embed the staff member's email, so
 * accidentally retrying with the same details reuses the existing profile
 * rather than creating a duplicate (avoids leaking orphan profiles when a
 * compensating delete itself failed, e.g. network blip).
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
    X, ArrowLeft, ArrowRight, Check, Loader2, AlertCircle,
    User, Eye, ShieldCheck,
} from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { groupUiViewKeys } from '../utils/uiViewSections';
import {
    createStaff,
    updateStaff,
    fetchStaffRoles,
    selectRoles,
} from '../store/slices/staffSlice';
import {
    createProfileApi,
    updateProfileApi,
    deleteProfileApi,
    getProfileApi,
    getProfilesApi,
    getPermissionsGroupedApi,
} from '../services/profileApi';
import {
    createUiViewProfileApi,
    updateUiViewProfileApi,
    deleteUiViewProfileApi,
    listUiViewProfilesApi,
    listUiViewKeysApi,
} from '../services/api';

// ── Per-member defaults ──────────────────────────────────────────────
// One safe read for each profile so a freshly-created team member can at
// least see the vehicle service records area without further setup.
//
// UI view keys are uppercase enum names from UiViewKey (e.g. SERVICE_RECORDS).
// Permission codes are lowercase "domain:action" strings from the Permission
// enum (e.g. vehicle:read). Mixing the cases will fail at Permission.fromCode.
const DEFAULT_UI_VIEW_KEYS = ['MY_PROFILE'];

/**
 * Groups this wizard does not show, to keep onboarding a team member short.
 *
 * ⚠️ HIDDEN HERE ONLY. Nothing is removed from the backend and nothing an existing
 * profile already holds is changed — the full pickers remain on the Staff Permission
 * Profiles and UI View Profiles pages for the rare case one of these is wanted. This
 * wizard is the everyday path and it was asking eleven questions nobody answers.
 *
 * What each one is, checked against the backend before hiding it:
 *
 *   PROCUREMENT, REPORT, DASHBOARD — ⚠️ these gate NOTHING. The codes are declared in
 *     Permission.java and not one endpoint in the product is @Secured with any of them,
 *     so ticking them has never granted anything. They are noise in the truest sense.
 *   STOCK — real endpoints, but Stock Management has no nav entry, so the feature is
 *     not reachable to grant access to.
 *   BILLING — real, and deliberately not a staff decision: subscription and payment
 *     methods are the owner's, and an OWNER holds every workshop permission implicitly.
 *   PROFILE — permission-profile administration. Editing who can do what is not
 *     something to hand out from inside the wizard that hands things out.
 *
 * The fleet portal permissions are NOT in this list: the backend no longer offers them
 * at all, because fleet logins are provisioned automatically with a fixed set. The one
 * fleet permission that IS a staff permission — fleet:manage, which creates those
 * logins — is still shown.
 */
const HIDDEN_PERMISSION_CATEGORIES = new Set([
    'procurement', 'report', 'dashboard', 'stock', 'billing', 'profile',
]);

/** Same reasoning, for the UI View step. Resource Planning is the only one of the three
 *  with a nav entry; Stock Management and Procurement have routes but no way to reach
 *  them, so granting the page grants nothing anyone can find. */
const HIDDEN_UI_VIEW_SECTIONS = new Set(['Resources & Inventory']);

/**
 * The two filters, exported so their spec exercises the real thing. A test that
 * re-declared these sets would keep passing after someone edited the wizard, which is
 * the drift this codebase has been bitten by more than once.
 */
export const visiblePermissionGroups = (groups) => (groups || []).filter(
    // Case-insensitive: the category is the permission's own prefix and its casing is
    // the backend's business, not something to depend on here.
    (g) => !HIDDEN_PERMISSION_CATEGORIES.has(String(g.category || '').toLowerCase()),
);

export const visibleUiViewSections = (availableKeys) =>
    groupUiViewKeys(availableKeys).filter((s) => !HIDDEN_UI_VIEW_SECTIONS.has(s.label));
const DEFAULT_PERMISSION_CODES = ['vehicle:read'];

const STEPS = [
    { id: 1, label: 'Details',     icon: User },
    { id: 2, label: 'UI Views',    icon: Eye },
    { id: 3, label: 'Permissions', icon: ShieldCheck },
];

const EMPTY_FORM = {
    firstName: '', lastName: '', email: '', mobile: '',
    addressLine1: '', addressLine2: '', suburb: '', state: '', postcode: '', country: 'Australia',
    emergencyContactName: '', emergencyContactPhone: '', emergencyContactRelationship: '',
    role: '', specialisation: '',
};

// Profile-naming helpers — email-suffixed so two staff with the same name
// don't collide on the unique name constraint.
const uiViewProfileNameFor   = (form) => `${form.firstName} ${form.lastName} (${form.email}) — UI Views`;
const permissionProfileNameFor = (form) => `${form.firstName} ${form.lastName} (${form.email}) — Permissions`;

const TeamMemberWizard = ({ isOpen, onClose, staff, isEditing, onSaved }) => {
    const dispatch = useAppDispatch();
    const roles = useAppSelector(selectRoles);

    // ── Wizard state ─────────────────────────────────────────────────
    const [step, setStep] = useState(1);
    const [staffUuid, setStaffUuid] = useState(null);
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [stepError, setStepError] = useState(null);
    const [stepSaved, setStepSaved] = useState(null); // transient 'Saved' toast text per step
    const [finished, setFinished] = useState(false);  // true after Step 3 save — locks the wizard into 'done' state

    // ── Profile UUIDs created/loaded for this team member ────────────
    const [uiViewProfileUuid, setUiViewProfileUuid] = useState(null);
    const [permissionProfileUuid, setPermissionProfileUuid] = useState(null);

    // ── Step 2 picker state ──────────────────────────────────────────
    const [availableKeys, setAvailableKeys] = useState([]);
    const [allowedViews, setAllowedViews] = useState(new Set());
    const [loadingKeys, setLoadingKeys] = useState(false);

    // ── Step 3 picker state ──────────────────────────────────────────
    const [permissionGroups, setPermissionGroups] = useState([]);
    const [selectedPermissions, setSelectedPermissions] = useState(new Set());
    const [loadingPerms, setLoadingPerms] = useState(false);

    // ── Reset state when (re)opened ──────────────────────────────────
    useEffect(() => {
        if (!isOpen) return;

        setStep(1);
        setErrors({});
        setStepError(null);
        setStepSaved(null);
        setFinished(false);
        setSubmitting(false);

        if (isEditing && staff) {
            setStaffUuid(staff.uuid);
            setFormData({
                firstName: staff.firstName || '',
                lastName: staff.lastName || '',
                email: staff.email || '',
                mobile: staff.mobile || '',
                addressLine1: staff.addressLine1 || '',
                addressLine2: staff.addressLine2 || '',
                suburb: staff.suburb || '',
                state: staff.state || '',
                postcode: staff.postcode || '',
                country: staff.country || 'Australia',
                emergencyContactName: staff.emergencyContactName || '',
                emergencyContactPhone: staff.emergencyContactPhone || '',
                emergencyContactRelationship: staff.emergencyContactRelationship || '',
                role: staff.role || '',
                specialisation: staff.specialisation || '',
            });
            setUiViewProfileUuid(staff.uiViewProfileUuid || null);
            setPermissionProfileUuid(staff.profileUuid || null);
        } else {
            setStaffUuid(null);
            setFormData(EMPTY_FORM);
            setUiViewProfileUuid(null);
            setPermissionProfileUuid(null);
        }
        setAllowedViews(new Set());
        setSelectedPermissions(new Set());

        dispatch(fetchStaffRoles());
    }, [isOpen, isEditing, staff, dispatch]);

    // Auto-clear the 'Saved' toast after a few seconds. The 'finished' state
    // doesn't time out — it stays visible until the user closes the wizard.
    useEffect(() => {
        if (!stepSaved || finished) return;
        const t = setTimeout(() => setStepSaved(null), 2500);
        return () => clearTimeout(t);
    }, [stepSaved, finished]);

    // ── Step 2 data load ─────────────────────────────────────────────
    useEffect(() => {
        if (!isOpen || step !== 2) return;

        let cancelled = false;
        (async () => {
            setLoadingKeys(true);
            setStepError(null);
            try {
                const keys = await listUiViewKeysApi();
                if (cancelled) return;
                setAvailableKeys(keys || []);

                if (uiViewProfileUuid) {
                    const all = await listUiViewProfilesApi();
                    if (cancelled) return;
                    const mine = (all || []).find(p => p.uuid === uiViewProfileUuid);
                    if (mine && mine.allowedViews) {
                        setAllowedViews(new Set(mine.allowedViews));
                    }
                }
            } catch (err) {
                if (!cancelled) {
                    setStepError(err.response?.data?.message || 'Failed to load UI view keys');
                }
            } finally {
                if (!cancelled) setLoadingKeys(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, step, uiViewProfileUuid]);

    // ── Step 3 data load ─────────────────────────────────────────────
    useEffect(() => {
        if (!isOpen || step !== 3) return;

        let cancelled = false;
        (async () => {
            setLoadingPerms(true);
            setStepError(null);
            try {
                const groups = await getPermissionsGroupedApi();
                if (cancelled) return;
                setPermissionGroups(visiblePermissionGroups(groups));

                if (permissionProfileUuid) {
                    const full = await getProfileApi(permissionProfileUuid);
                    if (cancelled) return;
                    setSelectedPermissions(new Set(full.permissions || []));
                }
            } catch (err) {
                if (!cancelled) {
                    setStepError(err.response?.data?.message || 'Failed to load permissions');
                }
            } finally {
                if (!cancelled) setLoadingPerms(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, step, permissionProfileUuid]);

    // ── Form handlers ────────────────────────────────────────────────
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
    };

    const validateStep1 = () => {
        const next = {};
        if (!formData.firstName.trim()) next.firstName = 'First name is required';
        if (!formData.lastName.trim()) next.lastName = 'Last name is required';
        if (!isEditing && !formData.email.trim()) next.email = 'Email is required';
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            next.email = 'Invalid email format';
        }
        if (!formData.role) next.role = 'Role is required';
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    // ── Idempotent profile lookup-or-create ──────────────────────────
    // If a profile with the desired name already exists for this org
    // (e.g. left behind by a previous failed attempt), reuse it instead
    // of failing on the unique-name constraint.
    const findOrCreateUiViewProfile = async (name) => {
        try {
            const created = await createUiViewProfileApi({
                name,
                description: `UI views for ${formData.firstName} ${formData.lastName}`,
                allowedViews: DEFAULT_UI_VIEW_KEYS,
            });
            return created.uuid;
        } catch (err) {
            const all = await listUiViewProfilesApi();
            const existing = (all || []).find(p => p.name === name);
            if (existing) return existing.uuid;
            throw err;
        }
    };

    const findOrCreatePermissionProfile = async (name) => {
        try {
            const created = await createProfileApi({
                name,
                description: `Permissions for ${formData.firstName} ${formData.lastName}`,
                permissions: DEFAULT_PERMISSION_CODES,
            });
            return created.uuid;
        } catch (err) {
            const res = await getProfilesApi({ page: 0, size: 50, search: name });
            const existing = (res?.content || []).find(p => p.name === name);
            if (existing) return existing.uuid;
            throw err;
        }
    };

    // ── Step 1 → Step 2 ──────────────────────────────────────────────
    const handleNextFromStep1 = async () => {
        if (!validateStep1()) return;
        setSubmitting(true);
        setStepError(null);

        if (isEditing && staffUuid) {
            try {
                await dispatch(updateStaff({ uuid: staffUuid, staffData: formData })).unwrap();
                setStepSaved('Details saved');
                setStep(2);
            } catch (err) {
                setStepError(typeof err === 'string' ? err : (err?.message || 'Failed to save details'));
            } finally {
                setSubmitting(false);
            }
            return;
        }

        // Create mode — create profiles first, then staff with both UUIDs.
        let createdUiUuid = null;
        let createdPermUuid = null;
        try {
            createdUiUuid = await findOrCreateUiViewProfile(uiViewProfileNameFor(formData));
            createdPermUuid = await findOrCreatePermissionProfile(permissionProfileNameFor(formData));

            const created = await dispatch(createStaff({
                ...formData,
                uiViewProfileUuid: createdUiUuid,
                profileUuid: createdPermUuid,
            })).unwrap();

            setStaffUuid(created.uuid);
            setUiViewProfileUuid(createdUiUuid);
            setPermissionProfileUuid(createdPermUuid);
            setStepSaved('Team member created');
            setStep(2);
        } catch (err) {
            const message = typeof err === 'string'
                ? err
                : (err?.response?.data?.message || err?.message || 'Failed to save details');

            // Compensating rollback of anything we created in THIS attempt.
            // Best-effort only — if rollback fails, idempotent reuse on retry
            // will pick up the same profile by name.
            if (createdPermUuid) {
                try { await deleteProfileApi(createdPermUuid); }
                catch (rollbackErr) { console.warn('Rollback of permission profile failed', rollbackErr); }
            }
            if (createdUiUuid) {
                try { await deleteUiViewProfileApi(createdUiUuid); }
                catch (rollbackErr) { console.warn('Rollback of UI view profile failed', rollbackErr); }
            }
            setStepError(message);
        } finally {
            setSubmitting(false);
        }
    };

    // ── Step 2 toggle handlers ───────────────────────────────────────
    const toggleView = (key) => {
        setAllowedViews(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    const toggleViewSection = (keys, selectAll) => {
        setAllowedViews(prev => {
            const next = new Set(prev);
            keys.forEach(k => selectAll ? next.add(k.key) : next.delete(k.key));
            return next;
        });
    };

    // ── Step 2 → Step 3 ──────────────────────────────────────────────
    const handleNextFromStep2 = async () => {
        if (!uiViewProfileUuid) {
            setStepError('No UI view profile attached');
            return;
        }
        setSubmitting(true);
        setStepError(null);
        try {
            await updateUiViewProfileApi(uiViewProfileUuid, {
                name: uiViewProfileNameFor(formData),
                description: `UI views for ${formData.firstName} ${formData.lastName}`,
                allowedViews: Array.from(allowedViews),
            });
            setStepSaved('UI views saved');
            setStep(3);
        } catch (err) {
            setStepError(err?.response?.data?.message || err?.message || 'Failed to save UI views');
        } finally {
            setSubmitting(false);
        }
    };

    // ── Step 3 toggle handlers ───────────────────────────────────────
    const togglePermission = (code) => {
        setSelectedPermissions(prev => {
            const next = new Set(prev);
            if (next.has(code)) next.delete(code); else next.add(code);
            return next;
        });
    };

    const toggleAllInGroup = (group, selectAll) => {
        setSelectedPermissions(prev => {
            const next = new Set(prev);
            group.permissions.forEach(p => selectAll ? next.add(p.code) : next.delete(p.code));
            return next;
        });
    };

    // ── Step 3 → Finish ──────────────────────────────────────────────
    // On success, the wizard stays open in a 'finished' state. The user
    // clicks Close to dismiss when they're ready. This gives them a chance
    // to step back and verify earlier choices before walking away.
    const handleFinish = async () => {
        if (!permissionProfileUuid) {
            setStepError('No permission profile attached');
            return;
        }
        if (selectedPermissions.size === 0) {
            setStepError('Select at least one permission');
            return;
        }
        setSubmitting(true);
        setStepError(null);
        try {
            await updateProfileApi(permissionProfileUuid, {
                name: permissionProfileNameFor(formData),
                description: `Permissions for ${formData.firstName} ${formData.lastName}`,
                permissions: Array.from(selectedPermissions),
            });
            setFinished(true);
            setStepSaved('All changes saved');
            if (onSaved) onSaved();
            // NOTE: do NOT call onClose() here — let the user dismiss.
        } catch (err) {
            setStepError(err?.response?.data?.message || err?.message || 'Failed to save permissions');
        } finally {
            setSubmitting(false);
        }
    };

    const handleBack = () => {
        if (step > 1) setStep(s => s - 1);
        setStepError(null);
    };

    // ── UI view keys grouped for display ─────────────────────────────
    const keySections = useMemo(() => visibleUiViewSections(availableKeys), [availableKeys]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
                {/* Backdrop is non-dismissive on purpose: the wizard contains in-progress
                    edits that have been saved per step, but accidentally clicking outside
                    used to drop the user out of the flow without warning. */}
                <div className="fixed inset-0 bg-black/50" />

                <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                    {/* Header */}
                    <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl z-10">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-gray-900">
                                {isEditing ? 'Edit Team Member' : 'Add Team Member'}
                            </h2>
                            <button
                                onClick={onClose}
                                disabled={submitting}
                                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="flex items-center gap-2">
                            {STEPS.map((s, idx) => {
                                const Icon = s.icon;
                                const isActive = step === s.id;
                                const isDone = step > s.id;
                                return (
                                    <React.Fragment key={s.id}>
                                        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium ${
                                            isActive ? 'bg-emerald-50 text-emerald-700' :
                                                isDone ? 'text-emerald-600' : 'text-gray-400'
                                        }`}>
                                            {isDone ? <Check size={14} /> : <Icon size={14} />}
                                            <span>Step {s.id}: {s.label}</span>
                                        </div>
                                        {idx < STEPS.length - 1 && (
                                            <div className={`flex-1 h-0.5 ${isDone ? 'bg-emerald-300' : 'bg-gray-200'}`} />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </div>

                    <div className="p-6 space-y-6">
                        {stepError && (
                            <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                                <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={18} />
                                <p className="text-sm text-red-700">{stepError}</p>
                            </div>
                        )}

                        {finished && (
                            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-start gap-3">
                                <Check className="text-emerald-600 flex-shrink-0 mt-0.5" size={18} />
                                <div>
                                    <p className="text-sm font-semibold text-emerald-800">
                                        {isEditing ? 'Team member updated' : 'Team member created'}
                                    </p>
                                    <p className="text-xs text-emerald-700 mt-0.5">
                                        Use Back to revise any step, or Close when you're done.
                                    </p>
                                </div>
                            </div>
                        )}

                        {stepSaved && !finished && (
                            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-2">
                                <Check className="text-emerald-600 flex-shrink-0" size={16} />
                                <p className="text-sm text-emerald-700">{stepSaved}</p>
                            </div>
                        )}

                        {step === 1 && (
                            <Step1Details
                                formData={formData}
                                errors={errors}
                                onChange={handleChange}
                                roles={roles}
                                isEditing={isEditing}
                            />
                        )}

                        {step === 2 && (
                            <Step2UiViews
                                fullName={`${formData.firstName} ${formData.lastName}`.trim()}
                                loading={loadingKeys}
                                keySections={keySections}
                                allowedViews={allowedViews}
                                onToggle={toggleView}
                                onToggleSection={toggleViewSection}
                            />
                        )}

                        {step === 3 && (
                            <Step3Permissions
                                fullName={`${formData.firstName} ${formData.lastName}`.trim()}
                                loading={loadingPerms}
                                permissionGroups={permissionGroups}
                                selectedPermissions={selectedPermissions}
                                onToggle={togglePermission}
                                onToggleGroup={toggleAllInGroup}
                            />
                        )}
                    </div>

                    <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex justify-between rounded-b-2xl">
                        <button
                            type="button"
                            onClick={step === 1 ? onClose : handleBack}
                            disabled={submitting}
                            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl transition-colors disabled:opacity-50"
                        >
                            {step === 1 ? (finished ? 'Close' : 'Cancel') : (<><ArrowLeft size={16} /> Back</>)}
                        </button>

                        {finished ? (
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={submitting}
                                className="flex items-center gap-2 px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-sm transition-colors disabled:opacity-50"
                            >
                                <Check size={16} />
                                Close
                            </button>
                        ) : step < 3 ? (
                            <button
                                type="button"
                                onClick={step === 1 ? handleNextFromStep1 : handleNextFromStep2}
                                disabled={submitting}
                                className="flex items-center gap-2 px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-sm transition-colors disabled:opacity-50"
                            >
                                {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
                                Next <ArrowRight size={16} />
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={handleFinish}
                                disabled={submitting}
                                className="flex items-center gap-2 px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-sm transition-colors disabled:opacity-50"
                            >
                                {submitting ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                                Finish
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ── Step 1: Team member details ──────────────────────────────────────
const Step1Details = ({ formData, errors, onChange, roles, isEditing }) => {
    const input = (err) =>
        `w-full px-3 py-2 border rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none ${
            err ? 'border-red-300' : 'border-gray-200'
        }`;
    return (
        <>
            <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Personal Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                        <input name="firstName" value={formData.firstName} onChange={onChange} className={input(errors.firstName)} />
                        {errors.firstName && <p className="mt-1 text-xs text-red-500">{errors.firstName}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                        <input name="lastName" value={formData.lastName} onChange={onChange} className={input(errors.lastName)} />
                        {errors.lastName && <p className="mt-1 text-xs text-red-500">{errors.lastName}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email {!isEditing && '*'}</label>
                        <input type="email" name="email" value={formData.email} onChange={onChange}
                               disabled={isEditing}
                               className={`${input(errors.email)} ${isEditing ? 'bg-gray-50' : ''}`} />
                        {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Mobile</label>
                        <input type="tel" name="mobile" value={formData.mobile} onChange={onChange} className={input()} />
                    </div>
                </div>
            </div>

            <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Address</h3>
                <div className="space-y-4">
                    <input name="addressLine1" value={formData.addressLine1} onChange={onChange}
                           placeholder="Address line 1" className={input()} />
                    <input name="addressLine2" value={formData.addressLine2} onChange={onChange}
                           placeholder="Address line 2" className={input()} />
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <input name="suburb" value={formData.suburb} onChange={onChange} placeholder="Suburb" className={input()} />
                        <input name="state" value={formData.state} onChange={onChange} placeholder="State" className={input()} />
                        <input name="postcode" value={formData.postcode} onChange={onChange} placeholder="Postcode" className={input()} />
                        <input name="country" value={formData.country} onChange={onChange} placeholder="Country" className={input()} />
                    </div>
                </div>
            </div>

            <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Emergency Contact</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <input name="emergencyContactName" value={formData.emergencyContactName} onChange={onChange}
                           placeholder="Name" className={input()} />
                    <input name="emergencyContactPhone" value={formData.emergencyContactPhone} onChange={onChange}
                           placeholder="Phone" className={input()} />
                    <input name="emergencyContactRelationship" value={formData.emergencyContactRelationship} onChange={onChange}
                           placeholder="Relationship" className={input()} />
                </div>
            </div>

            <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Role</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                        <select name="role" value={formData.role} onChange={onChange} className={input(errors.role)}>
                            <option value="">Select a role...</option>
                            {roles.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                        </select>
                        {errors.role && <p className="mt-1 text-xs text-red-500">{errors.role}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Specialisation</label>
                        <input name="specialisation" value={formData.specialisation} onChange={onChange}
                               placeholder="e.g., European cars" className={input()} />
                    </div>
                </div>
            </div>
        </>
    );
};

// ── Step 2: UI Views ─────────────────────────────────────────────────
const Step2UiViews = ({ fullName, loading, keySections, allowedViews, onToggle, onToggleSection }) => (
    <>
        <div>
            <h3 className="text-sm font-semibold text-gray-700">UI View Profile</h3>
            <p className="text-xs text-gray-500 mt-1">
                Choose which pages {fullName || 'this team member'} can see in the navigation.
                My Account is pre-selected.
            </p>
        </div>

        {loading ? (
            <div className="py-12 text-center">
                <Loader2 size={24} className="animate-spin mx-auto text-gray-400" />
            </div>
        ) : (
            <div className="space-y-5">
                {keySections.map(section => {
                    const allChecked = section.keys.every(k => allowedViews.has(k.key));
                    return (
                        <div key={section.label} className="border border-gray-200 rounded-xl p-4">
                            <div className="flex items-center justify-between mb-3">
                                <h4 className="text-sm font-semibold text-gray-700">{section.label}</h4>
                                <button
                                    type="button"
                                    onClick={() => onToggleSection(section.keys, !allChecked)}
                                    className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                                >
                                    {allChecked ? 'Deselect all' : 'Select all'}
                                </button>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {section.keys.map(k => (
                                    <label key={k.key} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 rounded-lg cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={allowedViews.has(k.key)}
                                            onChange={() => onToggle(k.key)}
                                            className="w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
                                        />
                                        <span className="text-sm text-gray-700">{k.label || k.key}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        )}
    </>
);

// ── Step 3: Permissions ──────────────────────────────────────────────
const Step3Permissions = ({ fullName, loading, permissionGroups, selectedPermissions, onToggle, onToggleGroup }) => (
    <>
        <div>
            <h3 className="text-sm font-semibold text-gray-700">Permission Profile</h3>
            <p className="text-xs text-gray-500 mt-1">
                Choose what {fullName || 'this team member'} can do inside the app.
                Vehicle read access is pre-selected.
            </p>
        </div>

        {loading ? (
            <div className="py-12 text-center">
                <Loader2 size={24} className="animate-spin mx-auto text-gray-400" />
            </div>
        ) : (
            <div className="space-y-5">
                {permissionGroups.map(group => {
                    const allChecked = group.permissions.every(p => selectedPermissions.has(p.code));
                    return (
                        <div key={group.category} className="border border-gray-200 rounded-xl p-4">
                            <div className="flex items-center justify-between mb-3">
                                <h4 className="text-sm font-semibold text-gray-700">{group.category}</h4>
                                <button
                                    type="button"
                                    onClick={() => onToggleGroup(group, !allChecked)}
                                    className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                                >
                                    {allChecked ? 'Deselect all' : 'Select all'}
                                </button>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {group.permissions.map(p => (
                                    <label key={p.code} className="flex items-start gap-2 px-3 py-2 hover:bg-gray-50 rounded-lg cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={selectedPermissions.has(p.code)}
                                            onChange={() => onToggle(p.code)}
                                            className="mt-0.5 w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
                                        />
                                        <div>
                                            <p className="text-sm text-gray-700">{p.label || p.code}</p>
                                            {p.description && (
                                                <p className="text-xs text-gray-500">{p.description}</p>
                                            )}
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        )}
    </>
);

export default TeamMemberWizard;